/**
 * MarkerInfoCard (0022) — an attractive map-marker card with a Google location
 * photo carousel, title, address, and the Maps link. Photos are fetched
 * client-side via the Places JS library (existing key), cached per place and
 * only when opened. Only the visible photo is loaded (so Place Photo billing is
 * per-viewed-image, not per-place).
 */
import React, { useEffect, useRef, useState } from 'react';
import { buildGoogleMapsUrlForMarker } from '../../utils/formatters';
import { useAuth } from '../../auth/AuthContext';
import { reportUsage } from '../../services/usageBeacon';

const _photoCache = new Map(); // key -> string[] of photo URLs ([] = looked up, none)

function cacheKey(m) {
  const la = Number(m.lat), lo = Number(m.lon);
  return `${m.title || ''}|${Number.isFinite(la) ? la.toFixed(4) : ''}|${Number.isFinite(lo) ? lo.toFixed(4) : ''}`;
}

const navBtnStyle = {
  pointerEvents: 'auto', width: 26, height: 26, borderRadius: '50%', border: 'none',
  cursor: 'pointer', background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 17,
  lineHeight: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center',
  boxShadow: '0 1px 4px rgba(0,0,0,0.45)', padding: 0,
};
const counterStyle = {
  pointerEvents: 'none', background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 11,
  fontWeight: 600, padding: '2px 8px', borderRadius: 10,
};

export default function MarkerInfoCard({ marker, map }) {
  const { lat, lon, title, popup, color } = marker;
  const address = (marker.address && String(marker.address).trim())
    || (popup && popup !== title ? String(popup).trim() : '');

  const { apiPaused } = useAuth();
  const key = cacheKey(marker);
  const [photos, setPhotos] = useState(() => _photoCache.get(key) || []);
  const [loading, setLoading] = useState(() => !_photoCache.has(key));
  const [idx, setIdx] = useState(0);
  const reqRef = useRef(0);

  useEffect(() => {
    setIdx(0);
    if (_photoCache.has(key)) {
      setPhotos(_photoCache.get(key));
      setLoading(false);
      return;
    }
    const places = window.google?.maps?.places;
    if (apiPaused || !map || !places?.PlacesService) {
      setLoading(false);
      return;
    }
    const myReq = ++reqRef.current;
    setLoading(true);
    const svc = new places.PlacesService(map);
    const query = [title, address].filter(Boolean).join(' ').trim() || title || 'place';
    const request = { query, fields: ['photos', 'place_id'] };
    if (Number.isFinite(Number(lat)) && Number.isFinite(Number(lon))) {
      request.locationBias = { lat: Number(lat), lng: Number(lon) };
    }
    reportUsage('google_maps', 'place_find');  // billable Find Place
    svc.findPlaceFromQuery(request, (results, status) => {
      if (myReq !== reqRef.current) return;
      let urls = [];
      try {
        if (status === places.PlacesServiceStatus.OK && results && results[0]?.photos?.length) {
          // getUrl() only builds a URL — the image is billed when an <img> loads it,
          // and we only render the current one, so navigating loads photos lazily.
          urls = results[0].photos.slice(0, 10).map((p) => p.getUrl({ maxWidth: 480, maxHeight: 280 }));
        }
      } catch (_e) { urls = []; }
      _photoCache.set(key, urls);
      setPhotos(urls);
      setLoading(false);
    });
    return () => { reqRef.current++; };
  }, [key, map, title, address, lat, lon, apiPaused]);

  const mapsUrl = buildGoogleMapsUrlForMarker({ lat, lon, address: popup, title });
  const accent = color || '#c49b63';
  const hasPhotos = photos.length > 0;
  const multi = photos.length > 1;
  const go = (d) => setIdx((i) => (i + d + photos.length) % photos.length);

  return (
    // Negative margins pull the card up/out of the Google InfoWindow's default
    // padding so there's no awkward blank border above the image, and the title
    // + link sit higher (visible without scrolling).
    <div style={{ width: 268, margin: '-10px -6px -4px' }}>
      <div style={{ position: 'relative', height: 132, borderRadius: 8, overflow: 'hidden', background: '#e9ecef' }}>
        {loading && (
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,#eceff3,#f6f8fa,#eceff3)' }} />
        )}
        {!loading && hasPhotos && (
          <img
            src={photos[idx]}
            alt={title || 'location'}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onLoad={() => reportUsage('google_maps', 'place_photo')}
            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
          />
        )}
        {!loading && !hasPhotos && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: accent, color: '#fff', fontSize: 12, opacity: 0.95 }}>
            No photo available
          </div>
        )}
        {multi && (
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, pointerEvents: 'none' }}>
            <button type="button" aria-label="Previous photo" onClick={(e) => { e.stopPropagation(); go(-1); }} style={navBtnStyle}>‹</button>
            <span style={counterStyle}>{idx + 1} / {photos.length}</span>
            <button type="button" aria-label="Next photo" onClick={(e) => { e.stopPropagation(); go(1); }} style={navBtnStyle}>›</button>
          </div>
        )}
      </div>
      <div style={{ padding: '7px 4px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: accent, flex: '0 0 auto' }} />
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a1a', lineHeight: 1.2 }}>{title}</div>
        </div>
        {address && (
          <div style={{ color: '#666', fontSize: 12, marginTop: 3 }}>{address}</div>
        )}
        {mapsUrl && (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'inline-block', marginTop: 8, padding: '5px 10px', borderRadius: 8, background: '#1a73e8', color: '#fff', fontSize: 12.5, fontWeight: 600, textDecoration: 'none' }}
          >
            View in Google Maps →
          </a>
        )}
      </div>
    </div>
  );
}
